/* eslint-disable react-hooks/exhaustive-deps */
import { useReducer, useMemo, useCallback, memo, useEffect } from 'react';
import SuperAdminContext from './SuperAdminContext';
import initialSuperAdminState from './initialSuperAdminState';
import superAdminReducer from './reducer/superAdmin.reducer';
import * as action from './reducer/superAdmin.actions';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import {
  IcreateMapFieldProps,
  ICreateQueryProps,
  IcreateUpdateTemplateProps,
  IShopifyQueryItem,
  IShopifyTemplateItem,
  IupdateMapFieldBodyProps,
  IUpdateQueryProps,
} from 'interfaces/superAdmin.interface';
import { ITenantUser } from 'interfaces/tenantUsers.interface';
import useContextUser from 'hooks/contexts/useContextUser';
import { superAdminRole } from 'utils/constants';
import { IChildrenProps } from 'interfaces/general.interface';
import { TgetConnectorsAPIBodyProps } from 'api/endpoints/meta/meta.response.interface';
import { abortTenantsController } from 'utils/abortController';

function SuperAdminProvider(props: IChildrenProps) {
  const [superAdminState, dispatch] = useReducer(
    superAdminReducer,
    initialSuperAdminState
  );
  const { dbUser } = useContextUser();
  const { getAccessTokenSilently } = useAuth0();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (
      !dbUser?.role ||
      dbUser.role !== superAdminRole ||
      superAdminState.tenants?.length > 0
    )
      return;
    (async () => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getClientsAction({
        dispatch,
        tenantsBody: superAdminState.tenantsBody,
        token,
        translate,
      });
    })();
  }, [dbUser?.role]);

  const getMoreTenants = useCallback(async () => {
    const token = await getAccessTokenSilently();
    const signal = await abortTenantsController();
    return action.getMoreTenantsAction({
      dispatch,
      tenantsBody: superAdminState.tenantsBody,
      signal,
      translate,
      token,
    });
  }, [superAdminState.tenantsBody]);

  const getTenantsWithUsers = useCallback(
    async ({ querySearch }: { querySearch: string }) => {
      const token = await getAccessTokenSilently();
      const signal = await abortTenantsController();
      return action.getTenantsWithUsersAction({
        dispatch,
        tenantsBody: superAdminState.tenantsBody,
        querySearch,
        signal,
        // tenants: superAdminState.tenants,
        // brandId,
        // userId,
        token,
        translate,
      });
    },
    [superAdminState.tenantsBody]
  );

  const removeUserClient = useCallback(
    (tenantId: string, userId: string) => {
      return action.removeUserClientAction({
        dispatch,
        tenants: superAdminState.tenants,
        tenantId,
        userId,
        translate,
      });
    },
    [superAdminState.tenants]
  );

  const addUserClient = useCallback(
    (tenantId: string, user: ITenantUser) => {
      if (superAdminState.tenants?.length === 0) return;
      return action.addUserClientAction({
        dispatch,
        tenants: superAdminState.tenants,
        tenantId,
        user,
      });
    },
    [superAdminState.tenants]
  );

  const getConnectors = useCallback(
    async ({ body }: { body: TgetConnectorsAPIBodyProps }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.getConnectorsAction({ dispatch, body, token, translate });
    },
    []
  );

  // Map fields //////////////////////////////////////////////////////////////
  const getMapFields = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getMapFieldsAction({
      dispatch,
      token,
      translate,
    });
  }, []);

  const createMapField = useCallback(async ({ fieldData }: IcreateMapFieldProps) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.createMapFieldAction({
      dispatch,
      fieldData,
      token,
      translate,
    });
  }, []);

  const updateMapField = useCallback(
    async ({ filter, update }: IupdateMapFieldBodyProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateMapFieldAction({
        dispatch,
        body: { filter, update },
        token,
        translate,
      });
    },
    []
  );

  const deleteMapField = useCallback(async ({ mapFieldId }: { mapFieldId: string }) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.deleteMapFieldAction({
      dispatch,
      mapFieldId,
      token,
      translate,
    });
  }, []);

  // Map fields templates
  const getMapFieldsTemplates = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getMapFieldsTemplatesAction({
      dispatch,
      token,
      translate,
    });
  }, []);

  const createMapFieldsTemplate = useCallback(
    async ({ tag, map_field_ids }: { tag: string; map_field_ids: string[] }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createMapFieldsTemplateAction({
        dispatch,
        tag,
        map_field_ids,
        token,
        translate,
      });
    },
    []
  );

  const deleteMapFieldTemplate = useCallback(
    async ({ templateName }: { templateName: string }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deleteMapFieldTemplateAction({
        dispatch,
        templateName,
        token,
        translate,
      });
    },
    []
  );

  // Queries Shopify /////////////////////////////////////////////////////////
  const getShopifyQueries = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getShopifyQueriesAction(dispatch, token, translate);
  }, []);

  const selectShopifyQuery = useCallback((query: IShopifyQueryItem) => {
    return action.selectShopifyQueryAction(dispatch, query);
  }, []);

  const createShopifyQuery = useCallback(
    async ({
      tag,
      description,
      query,
      indexable,
      scope,
      audience,
    }: ICreateQueryProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createShopifyQueryAction({
        dispatch,
        values: { tag, description, query, indexable, scope, audience },
        token,
        translate,
      });
    },
    []
  );

  const deleteShopifyQuery = useCallback(async ({ queryId }: { queryId: string }) => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.deleteShopifyQueryAction({
      dispatch,
      queryId,
      token,
      translate,
    });
  }, []);

  const updateShopifyQuery = useCallback(
    async ({
      queryId,
      query,
      tag,
      description,
      indexable,
      scope,
      audience,
      showSuccessMsg,
    }: IUpdateQueryProps & { showSuccessMsg?: boolean }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.updateShopifyQueryAction({
        dispatch,
        dataToUpdate: { queryId, query, tag, description, indexable, scope, audience },
        showSuccessMsg,
        token,
        translate,
      });
    },
    []
  );

  // Templates Shopify
  const getShopifyTemplates = useCallback(async () => {
    const token = await getAccessTokenSilently();
    if (!token) return false;
    return await action.getShopifyTemplatesAction({ dispatch, token, translate });
  }, []);

  const selectShopifyTemplate = useCallback(
    ({ template }: { template: IShopifyTemplateItem }) => {
      return action.selectShopifyTemplateAction({ dispatch, template });
    },
    []
  );

  const createUpdateShopifyTemplate = useCallback(
    async ({ tag, queries }: IcreateUpdateTemplateProps) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.createUpdateShopifyTemplateAction({
        dispatch,
        values: { tag, queries },
        token,
        translate,
      });
    },
    []
  );

  const deleteShopifyTemplate = useCallback(
    async ({ templateTag }: { templateTag: string }) => {
      const token = await getAccessTokenSilently();
      if (!token) return false;
      return await action.deleteShopifyTemplateAction({
        dispatch,
        templateTag,
        token,
        translate,
      });
    },
    []
  );

  const memoProvider = useMemo(
    () => ({
      ...superAdminState,
      removeUserClient,
      addUserClient,
      getConnectors,
      getMoreTenants,
      getTenantsWithUsers,
      // Map fields
      getMapFields,
      createMapField,
      updateMapField,
      deleteMapField,
      // Map fields templates
      getMapFieldsTemplates,
      createMapFieldsTemplate,
      deleteMapFieldTemplate,
      shopify: {
        ...superAdminState.shopify,
        // Queries
        getShopifyQueries,
        selectShopifyQuery,
        createShopifyQuery,
        deleteShopifyQuery,
        updateShopifyQuery,
        // Templates
        getShopifyTemplates,
        selectShopifyTemplate,
        createUpdateShopifyTemplate,
        deleteShopifyTemplate,
      },
    }),
    [
      superAdminState,
      removeUserClient,
      addUserClient,
      getConnectors, // Map fields
      getMoreTenants,
      getTenantsWithUsers,
      getMapFields,
      createMapField,
      updateMapField,
      deleteMapField,
      // Map fields templates
      getMapFieldsTemplates,
      createMapFieldsTemplate,
      deleteMapFieldTemplate,
      // Queries
      getShopifyQueries,
      selectShopifyQuery,
      createShopifyQuery,
      deleteShopifyQuery,
      updateShopifyQuery,
      // Templates
      getShopifyTemplates,
      selectShopifyTemplate,
      createUpdateShopifyTemplate,
      deleteShopifyTemplate,
    ]
  );

  return (
    <SuperAdminContext.Provider value={memoProvider}>
      {props.children}
    </SuperAdminContext.Provider>
  );
}

export default memo(SuperAdminProvider);
